import _ from "lodash";
import React, { useEffect } from "react";

// components
import Loading from "UIComponents/Loading";
import ListSelection from "../ListSelection";
import RemoveSubjectModal from "AppComponents/RemoveSubjectModal";
import FieldComponent from "UnitPlans/components/IBcomponents/FieldComponent";

// design system
import { fontStyles } from "@toddle-edu/ds-theme";

// third party libs.
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";

// utils
import { getFieldValue } from "Assessments/modules/Utils";
import {
  getTypeToRender,
  getFieldComponentProps,
  getIsDynamicField,
  shouldUpdateSubject,
  getGroupedUnitPlanUpdateParams,
} from "../../modules/Utils";
import { getFieldOptionsLocale } from "UnitPlans/components/LibraryCommonComponents/ResourceEdit/modules/Utils";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";

// constants
import {
  INTERDISCIPLINARY_FIELD_MAPPING,
  ELEMENT_TYPE_ADD_LEARNING_GOALS,
} from "ClassRoom/components/AddAssignment/LessonPlanningAssistant/modules/Constants";
import {
  ELEMENT_TYPE_UNIT_PLAN,
  ASSESSMENT,
  MYP_SUBJECT_SPECIFIC,
} from "Constants/stringConstants";

// modules
import {
  updateAssignmentField,
  updateUnitPlanField,
} from "ClassRoom/components/AddAssignment/LessonPlanningAssistant/modules/LessonPlanningAssistantModule";
import {
  getCurrentCurriculumProgramType,
  getSelectedCurriculumId,
  getSelectedAcademicYear,
  getOrganizationId,
} from "modules/CommonSelectors";
import {
  getPlannerElementSetsOfCurriculumEnhancer,
  getPYPplannerElementNodeOfCurriculumEnhancer,
} from "modules/CommonQueryEnhancers";
import { getPlannerFieldOptions } from "UnitPlans/modules/UnitPlanModule";
import { updateConversationField } from "ClassRoom/components/AddAssignment/AiQuestionAssistant/modules/AiQuestionAssistantModule";
import { getRemovedSubjectDependencies } from "Assessments/modules/AssessmentModule";

// css
import classes from "./ContextSelectionItem.scss";

const styles = {
  fieldComponentStyles: {
    labelStyle: {
      lineHeight: "2.4rem",
    },
    labelContainerStyle: {
      marginBottom: "0px",
    },
  },
  subTextStyle: {
    fontSize: "1.4rem",
    lineHeight: "2rem",
    marginBottom: "8px",
    ...fontStyles.medium,
  },
  labelWithMarginStyle: {
    lineHeight: "2.4rem",
    marginBottom: "8px",
  },
};

const ContextSelectionItem = props => {
  const {
    t,
    type,
    field,
    fieldConfig,
    assessmentDetails,
    updateAssignmentField,
    portalType,
    fieldList,
    fieldValue,
    unitPlanFieldList,
    isUnitDependent,
    updateUnitPlanField,
    context,
    onClose,
    unitPlanFields,
    allFields,
    middlePaneRef,
    mode,
    curriculumType,
    sourceFrom,
    isFromUnitPlan,
    plannerElements,
    unitPlanId,
    isDynamicField,
    unitPlanTemplate,
    selectedAcademicYear,
    organizationId,
    curriculumId,
    course,
    fieldRef,
    updateParams,
    setUpdateParams,
    localContext,
    setLocalContext,
    onClickSave,
    assessmentFieldList,
    templateId,
    subjectDependenciesObj,
    updateSubjectDependenciesObj,
    isQuestionAssistant,
    isAIAssessmentTools,
    updateConversationField,
    conversationMetadata,
    aiConversationId,
    updateInputField,
    getRemovedSubjectDependencies,
    learningCourseId,
    isUnitPlanAI,
    isPlannerElementDataSet,
    isData,
    isLoading,
  } = props;

  const isDataLoading = !isData && isLoading;

  // in case of learning course unit will be selected in local state
  const value =
    localContext?.id && field === "unitPlan"
      ? localContext?.id
      : _.get(updateParams, `${field}`);

  // we update the local state of the field whenever the field changes because user can cancel the changes
  // on save click we update the actual context state in the parent component
  useEffect(() => {
    setLocalContext(context?.[field] || "");
  }, [field]);

  // Set the initial value of the field in local state
  useEffect(() => {
    // for field with subFields create local value like
    // value: {inquiryQuestion1: [], inquiryQuestion2: [], inquiryQuestion3: []}
    if (_.has(fieldList[field], "subFields")) {
      const subFields = _.get(fieldList[field], "subFields");

      const fieldValues = _.reduce(
        subFields,
        (acc, subField) => {
          const uid = `${field}_${subField.key}`;
          const fieldValue = getFieldValue({
            fields: allFields,
            uid,
          });

          if (!acc[subField.key]) {
            acc[subField.key] = [];
          }

          if (_.isArray(fieldValue)) {
            if (subField?.type === "RichText") {
              acc[subField.key] = !_.isEmpty(fieldValue) ? fieldValue : "";
            } else {
              acc[subField.key].push(...(fieldValue || []));
            }
          } else {
            acc[subField.key] = fieldValue;
          }
          return acc;
        },
        {}
      );

      setUpdateParams({
        [`${field}`]: fieldValues,
      });

      return;
    }
    // for fields those keys contain subject id or are dynamic fields
    // like disciplinaryObjectivesMYP_12345, disciplinaryObjectivesMYP_67890 etc
    if (isDynamicField) {
      const interdisciplinaryFieldUID = INTERDISCIPLINARY_FIELD_MAPPING[field];

      const fields = _.filter(unitPlanFields, ({ uid }) => {
        return _.startsWith(uid, interdisciplinaryFieldUID);
      });

      const values = _.compact(_.flattenDeep(_.map(fields, "value")));

      // for unit plans, we need to store each field value in a separate key
      // like disciplinaryObjectivesMYP_12345: [], disciplinaryObjectivesMYP_67890: []
      if (isUnitPlanAI) {
        const interdisciplinaryFieldValues = _.reduce(
          fields,
          (result, field) => {
            // except the exact interdisciplinaryFieldUID, that is unused
            if (field.uid === interdisciplinaryFieldUID) {
              return result;
            }

            const fieldValue = getFieldValue({
              fields: unitPlanFields,
              uid: field.uid,
            });

            result[field.uid] = fieldValue;
            return result;
          },
          {}
        );

        setUpdateParams({
          [`${field}`]: values,
          ...interdisciplinaryFieldValues,
        });
        return;
      }

      setUpdateParams({
        [`${field}`]: values,
      });
    } else {
      switch (field) {
        case "pedagogicalApproachDP": {
          if (_.every(fieldValue, item => _.has(item, "value"))) {
            const selectedValues = _.filter(fieldValue, item =>
              _.get(item, "isSelected", false)
            );

            setUpdateParams({
              [`${field}`]: _.map(selectedValues, "id"),
            });
          } else {
            setUpdateParams({
              [`${field}`]: fieldValue,
            });
          }

          break;
        }
        default: {
          switch (type) {
            case "RichText":
              setUpdateParams({
                [`${field}`]: !_.isEmpty(fieldValue) ? fieldValue : "",
              });
              break;
            default: {
              setUpdateParams({
                [`${field}`]: fieldValue,
              });
            }
          }
        }
      }
    }
  }, [field]);

  const setCustomRef = (key, ref) => {
    fieldRef.current[key] = ref;

    if (type === ELEMENT_TYPE_ADD_LEARNING_GOALS) {
      setTimeout(() => {
        ref?.onEditClick?.(key);

        if (isUnitDependent) {
          ref?.learningGoalsModalRef?.current?.handleToggleSwitch?.();
        }
      });
    }
  };

  const getPopupContainer = () => document.body;

  const onCloseRemoveSubjectModal = () => {
    updateSubjectDependenciesObj({
      subtitle: "",
      subjectDependencies: [],
    });
  };

  const renderBody = () => {
    if (_.isEmpty(updateParams) && type !== ELEMENT_TYPE_ADD_LEARNING_GOALS) {
      return <Loading />;
    }

    if (isUnitPlanAI && isDataLoading) {
      return (
        <div className={classes.loadingContainer}>
          <Loading />
        </div>
      );
    }

    // Local fields specific to AI just to get user input for specific prompts (like location, geometric tools etc)
    if (fieldConfig?.isLocalField) {
      const { type, fieldTypeConfig, placeholder, options, ...restParams } =
        fieldConfig || {};

      if (type === "FieldDropdown") {
        return (
          <ListSelection
            value={localContext}
            options={options}
            updateInputField={e => setLocalContext(e?.params)}
            multi={fieldTypeConfig?.multi}
          />
        );
      } else {
        return (
          <FieldComponent
            type={type}
            value={localContext}
            updateInputField={e => setLocalContext(e?.params)}
            placeholder={placeholder ? placeholder : null}
            defaultOptions={options}
            fieldTypeConfig={fieldTypeConfig}
            getPopupContainer={getPopupContainer}
            {..._.omit(restParams, "label")}
          />
        );
      }
    }

    if (type) {
      const isLearningGoalsType = type === ELEMENT_TYPE_ADD_LEARNING_GOALS;

      const _field = isLearningGoalsType ? "addLearningGoals" : field;
      const _fieldList = isLearningGoalsType ? assessmentFieldList : fieldList;

      const {
        config: { subtext, fieldTypeConfig = {}, options = [] } = {},
      } = _fieldList[_field] ? _fieldList[_field] : {};

      const { label, ...templateFieldConfig } = _.get(
        fieldList,
        `${field}.config`,
        {}
      );

      const localisedOptions = getFieldOptionsLocale({
        t,
        key: _field,
        options,
      });

      let defaultOptions = middlePaneRef?.getOptions({
        fieldKey: _field,
        defaultOptions: localisedOptions,
        fieldObj: _fieldList[_field],
        value,
        curriculumType,
      });

      if (isUnitDependent && !isLearningGoalsType) {
        const objectiveLevelNodes = _.get(
          _.find(unitPlanFields, { uid: "objectiveLevel" }),
          "resolvedMinimalTree.nodes",
          []
        );

        defaultOptions = getPlannerFieldOptions({
          t,
          plannerElements,
          unitPlanFields,
          fieldKey: _field,
          field_list: unitPlanFieldList,
          courseId: course?.id,
          selectedAcademicYear,
          curriculumId,
          organizationId,
          grades: _.map(course?.grades, "id", []),
          curriculumType,
          objectiveLevelNodes,
        });
      }

      const assessmentType = getFieldValue({
        fields: assessmentDetails?.allFields,
        uid: "assessmentType",
      });

      const fieldComponentExtraProps = getFieldComponentProps({
        t,
        field,
        type,
        value,
        allFields,
        unitPlanFields,
        defaultOptions,
        assessmentDetails,
        fieldTypeConfig,
        isUnitDependent,
        unitPlanTemplate,
        fieldList: _fieldList,
      });

      const fieldData = _.find(allFields, { uid: field });
      const fieldId = _.get(fieldData, "id", "");

      const updateSubjectDependencies = funParams => {
        const { params, extraParams, fieldKey } = funParams || {};

        const updatedSubjectIds = _.get(params, "subjects", []);
        const unitPlanSubjectIds = _.get(extraParams, "subjects.value", []);
        const assessmentSubjectIds = getFieldValue({
          fields: allFields,
          uid: "subjects",
        });

        const isLearningGoalsEvaluationEnabled = _.get(
          assessmentDetails,
          "isLearningGoalsEvaluationEnabled",
          false
        );

        const isUnitPlanField = fieldKey === "unitPlan";
        const removedSubjectIds = _.difference(
          assessmentSubjectIds,
          unitPlanSubjectIds
        );

        const updatedUnitType = getFieldValue({
          fields: extraParams?.allFields,
          uid: "unitType",
        });

        // current unit plan
        const selectedUnitPlan = getFieldValue({
          fields: allFields,
          uid: "unitPlan",
        });

        if (
          isUnitPlanField &&
          _.isEmpty(removedSubjectIds) &&
          updatedUnitType != MYP_SUBJECT_SPECIFIC
        ) {
          onCloseRemoveSubjectModal();
          return false;
        }

        const updatedSubjectValue = isUnitPlanField
          ? shouldUpdateSubject(unitPlanSubjectIds)
            ? unitPlanSubjectIds
            : []
          : updatedSubjectIds;

        const isUnitChangeFromEmptyToSubjectSpecific =
          _.isEmpty(selectedUnitPlan) &&
          updatedUnitType === MYP_SUBJECT_SPECIFIC;

        const {
          subTitle: subtitle,
          subjectDependencies,
          headerTitle,
        } = getRemovedSubjectDependencies({
          t,
          curriculumType,
          assessmentTools: _.get(assessmentDetails, "assessmentTools"),
          assessmentFields: allFields,
          field_list: fieldList,
          templateId,
          currentSubjectValue: assessmentSubjectIds,
          updatedSubjectValue: updatedSubjectValue,
          assessmentMode: mode,
          assessmentType,
          isLearningGoalsEvaluationEnabled,
          updatedUnitType,
          sourceFrom: isUnitPlanField ? "UNIT_SELECTION_DROPDOWN" : "",
          isUnitChangeFromEmptyToSubjectSpecific,
        });

        if (!_.isEmpty(subjectDependencies)) {
          updateSubjectDependenciesObj({
            headerTitle,
            subtitle,
            subjectDependencies,
            params,
            extraParams,
          });

          return true;
        }

        return false;
      };

      const updateInputFieldLocal = (...args) => {
        const item = _.first(args);
        const { params, extraParams } = item || {};

        const isRemoveSubjectModalConfirmed = !_.isEmpty(
          subjectDependenciesObj?.subjectDependencies
        );

        const isRemoveSubjectModalVisible =
          _.first(_.keys(item?.params)) === _field &&
          updateSubjectDependencies({
            params,
            extraParams,
            fieldKey: _field,
          });

        if (isPlannerElementDataSet) {
          if (isDynamicField) {
            // For dynamic fields, we need to group the params
            const groupedParams = getGroupedUnitPlanUpdateParams({
              updateParams,
              params,
              extraParams,
            });

            setUpdateParams({ ...groupedParams, extraParams });
            onClickSave({
              params: groupedParams,
              extraParams,
              shouldCloseModal: false,
              isDynamicField,
            });
          } else {
            setUpdateParams({ ...params, extraParams });
            onClickSave({ params, extraParams, shouldCloseModal: false });
          }

          return;
        }

        switch (_field) {
          case "subjects": {
            // CASE 1 : Update when modal is not shown
            // CASE 2 : Update when modal is shown and user has confirmed to remove the subject
            if (isRemoveSubjectModalConfirmed || !isRemoveSubjectModalVisible) {
              setUpdateParams({ ...params, extraParams });
              onCloseRemoveSubjectModal();
            }

            break;
          }

          case "unitPlan": {
            if (getIsLearningCoursesEnabled()) {
              const { title, image } = extraParams || {};

              if (params?.unitPlan) {
                setLocalContext({
                  id: params?.unitPlan,
                  title: title || "Untitled",
                  image,
                });
              } else {
                setLocalContext({});
              }

              setUpdateParams(params);
            } else {
              // CASE 1 : Update when modal is not shown
              // CASE 2 : Update when modal is shown and user has confirmed to remove the subject
              if (
                isRemoveSubjectModalConfirmed ||
                !isRemoveSubjectModalVisible
              ) {
                middlePaneRef?.updateField(params);
                setUpdateParams(params);
                onCloseRemoveSubjectModal();
              }
            }

            break;
          }
          case "addLearningGoals": {
            setUpdateParams(params);

            const commonParams = {
              t,
              ...params,
              ...item,
              isDebounce: false,
              portalType,
            };

            if (isQuestionAssistant) {
              let _conversationMetadata = conversationMetadata;

              if (isAIAssessmentTools) {
                /**
                 * @description
                 * For AI Assessment tools:
                 * 1. By default, the system combines learning goals from all questions
                 * 2. When user manually edits learning goals, we set conversationMetadata.isEditedByUserManually = true
                 * 3. After manual modification, learning goals will only update through user actions
                 * 4. Automatic updates from question learning goal changes will be disabled
                 */
                _conversationMetadata = {
                  ...conversationMetadata,
                  isEditedByUserManually: true,
                };
              }

              updateConversationField({
                ...commonParams,
                fieldUID: field,
                fieldList,
                conversationMetadata: _conversationMetadata,
                aiConversationId,
              });
            } else if (isUnitDependent) {
              updateUnitPlanField({
                ...commonParams,
                unitPlanFields,
                field_list: unitPlanFieldList,
                unitPlanId,
              });
            } else {
              updateAssignmentField({
                ...commonParams,
                assessmentDetails,
                field_list: fieldList,
              });
            }

            break;
          }
          // directly update the field without having a local state
          case "relatedConcepts": {
            setUpdateParams(params);
            onClickSave({ params, extraParams, shouldCloseModal: false });
            break;
          }
          default: {
            setUpdateParams({ ...params, extraParams });
          }
        }
      };

      const fieldComponentStyles = !_.isEmpty(subtext)
        ? styles.fieldComponentStyles
        : {
            ...styles.fieldComponentStyles,
            labelStyle: styles.labelWithMarginStyle,
          };

      const showListSelection =
        type === "FieldDropdown" && field !== "unitPlan";

      const showLabel =
        type !== ELEMENT_TYPE_ADD_LEARNING_GOALS && field !== "unitPlan";

      return (
        <>
          {showListSelection ? (
            <ListSelection
              fieldKey={field}
              value={fieldComponentExtraProps.value}
              options={defaultOptions}
              updateInputField={updateInputFieldLocal}
              multi={fieldTypeConfig?.multi}
              // subjects in unit plan only accept array and not string
              isValueArray={isUnitPlanAI && field === "subjects"}
            />
          ) : (
            <FieldComponent
              t={t}
              templateId={templateId}
              portalType={portalType}
              assessmentMode={mode}
              assessmentType={assessmentType}
              unitPlanId={unitPlanId}
              parentId={isUnitDependent ? unitPlanId : assessmentDetails?.id}
              parentType={isUnitDependent ? ELEMENT_TYPE_UNIT_PLAN : ASSESSMENT}
              resolvedValue={_.get(fieldData, "resolvedMinimalTree", "")}
              fieldId={fieldId}
              subText={subtext}
              updateInputField={updateInputFieldLocal}
              unitPlanFields={unitPlanFields}
              unitPlanFieldList={unitPlanFieldList}
              defaultOptions={defaultOptions}
              onClose={onClose}
              sourceFrom={sourceFrom}
              isFromUnitPlan={isFromUnitPlan}
              styles={fieldComponentStyles}
              subTextStyle={styles.subTextStyle}
              curriculumType={curriculumType}
              field_list={fieldList}
              customRef={setCustomRef}
              selectedCourseId={course?.id}
              fieldTypeConfig={fieldTypeConfig}
              getPopupContainer={getPopupContainer}
              label={showLabel ? label : ""}
              {...templateFieldConfig}
              {...fieldComponentExtraProps}
              assessmentDetails={assessmentDetails}
              learningCourseId={learningCourseId}
              {...(isPlannerElementDataSet ? { onBackClick: onClose } : {})}
            />
          )}

          {!_.isEmpty(subjectDependenciesObj?.subjectDependencies) ? (
            <div className={classes.subjectDependenciesContainer}>
              <RemoveSubjectModal
                parentId={assessmentDetails?.id}
                parentType={ASSESSMENT}
                portalType={portalType}
                updateInputField={(params, extraParams) =>
                  updateInputField({ params, extraParams })
                }
                removeButtonLabel={t("common:remove")}
                onRemove={() => updateInputFieldLocal(subjectDependenciesObj)}
                onClose={onCloseRemoveSubjectModal}
                fieldList={assessmentFieldList}
                {...(subjectDependenciesObj || {})} // subtitle and subjectDependencies
              />
            </div>
          ) : null}
        </>
      );
    }

    return null;
  };

  return renderBody();
};

const mapStateToProps = (state, ownProps) => {
  const {
    field,
    config,
    fieldList,
    allFields,
    unitPlanDetails = {},
    unitPlanTemplate,
    isUnitDependent,
    assessmentFieldList,
    assessmentFields,
    isPlannerElementDataSet,
  } = ownProps;

  const { field_list: unitPlanFieldList } = unitPlanTemplate || {};
  const { unitPlanId, unitPlanFields } = unitPlanDetails || {};

  const fieldValue = getFieldValue({
    fields: isUnitDependent ? unitPlanFields : assessmentFields,
    uid: field,
  });

  const selectedAcademicYear = getSelectedAcademicYear(state);
  const organizationId = getOrganizationId(state);
  const curriculumType = getCurrentCurriculumProgramType(state);
  const curriculumId = getSelectedCurriculumId(state);
  const curriculum = _.find(state.platform.organizationCurriculumPrograms, {
    type: curriculumType,
    id: curriculumId,
  });

  const currentFieldConfig = _.get(fieldList, `${field}.config`, {});

  const currentFieldPlannerElementConfig = _.get(
    currentFieldConfig,
    `plannerElementConfig`,
    {}
  );

  const plannerElementTypes = _.get(currentFieldPlannerElementConfig, "type");

  const type = getTypeToRender({
    fields: [{ field, config }],
    fieldList,
    assessmentFieldList,
    unitPlanFieldList,
    isUnitDependent,
  });

  const isDynamicField = getIsDynamicField({
    field,
    isUnitDependent,
    allFields,
  });

  return {
    type,
    fieldValue,
    curriculumType,
    curriculumId,
    curriculumNodeInterfaceType: _.get(curriculum, "nodeInterfaceType", ""),
    unitPlanId,
    unitPlanTemplate,
    unitPlanFieldList,
    plannerElementTypes,
    isStepWithPlannerElement: currentFieldConfig?.type === "PLANNER_ELEMENT",
    isDynamicField,
    organizationId,
    selectedAcademicYear,
    // skip getPlannerElementSetsOfCurriculumEnhancer as it is
    // internally called in AddLearningGoals and PlannerElementDataSet
    skipQuery:
      type === ELEMENT_TYPE_ADD_LEARNING_GOALS || isPlannerElementDataSet,
  };
};

const mapActionCreators = {
  updateAssignmentField,
  updateUnitPlanField,
  updateConversationField,
  getRemovedSubjectDependencies,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  getPlannerElementSetsOfCurriculumEnhancer,
  getPYPplannerElementNodeOfCurriculumEnhancer
)(ContextSelectionItem);
